/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Form, Row, Select, Space, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

// Misc imports
import { ANALYTIC_TAGS_RECOMMENDATIONS } from '../../../../constants/flowGraph';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_CURLY_DOT_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Text } = Typography;

const AnalyticTagsNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const [addTagsSearchValue, setAddTagsSearchValue] = useState('');
  const [removeTagsSearchValue, setRemoveTagsSearchValue] = useState('');
  const [addTagsSelectOpen, setAddTagsSelectOpen] = useState(false);
  const [removeTagsSelectOpen, setRemoveTagsSelectOpen] = useState(false);

  // Custom validator to check if at least one of the two fields is filled
  const atLeastOneRequired = ({ getFieldValue }) => ({
    validator(rule, value) {
      if (getFieldValue(['data', 'analyticTags'])?.length > 0 || getFieldValue(['data', 'analyticTagsRemoved'])?.length > 0) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(t('At least one of the two fields is required')));
    }
  });

  // Create a handler that checks the typed or pasted text and closes the dropdown on error
  const handleTagText = (fieldName, closeDropdown) => (e) => {
    const pastedData = e.clipboardData?.getData('text'); //if pastedData exists, use it; otherwise, use e.key
    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_CURLY_DOT_REGEX.test(pastedData ? pastedData : e.key)) {
      e.preventDefault();
      const error = t('Only alphanumeric characters, hyphens, underscores, dots and curly braces are allowed');
      closeDropdown();
      form.setFields([
        {
          name: fieldName,
          errors: [error]
        }
      ]);
    } else {
      form.setFields([
        {
          name: fieldName,
          errors: []
        }
      ]);
    }
  };

  // Handler for Add Tags Select
  const handleAddTagsText = handleTagText(['data', 'analyticTags'], () => setAddTagsSelectOpen(false));
  // Handler for Remove Tags Select
  const handleRemoveTagsText = handleTagText(['data', 'analyticTagsRemoved'], () => setRemoveTagsSelectOpen(false));

  return (
    <Fragment>
      <Form.Item
        label={
          <Row>
            <Col span={24}>
              <Space size={4}>
                <CheckOutlined className="text-success" />
                <Text strong>{t('Add Tags:')}</Text>
              </Space>
            </Col>

            <Col span={24}>
              <Text type="secondary">{t('Add one or more tags to Agent records, after this node.')}</Text>
            </Col>
          </Row>
        }
        name={['data', 'analyticTags']}
        rules={[atLeastOneRequired]}
      >
        <Select
          mode="tags"
          size="large"
          autoClearSearchValue={false}
          onSearch={setAddTagsSearchValue}
          searchValue={addTagsSearchValue}
          placeholder={t('Select or enter a tag')}
          className="scrollable-dropdown-input"
          // We added the `dropdownAlign` in order to avoid the overlap between tags list and input field
          // found that here: https://github.com/ant-design/ant-design/issues/12402#issuecomment-469958721
          dropdownAlign={{
            overflow: { adjustY: 0 }
          }}
          options={Object.values(ANALYTIC_TAGS_RECOMMENDATIONS).map(recommendation => ({ label: recommendation, value: recommendation }))}
          onChange={() => {
            setAddTagsSearchValue('');
            setTimeout(() => {
              // Hack to validate the other field too
              form.validateFields();
            }, 500);
          }}
          open={addTagsSelectOpen}
          onDropdownVisibleChange={(open) => {
            if (form.getFieldError(['data', 'analyticTags']).length > 0) {
              setAddTagsSelectOpen(false);
            } else {
              setAddTagsSelectOpen(open);
            }
          }}
          onInputKeyDown={handleAddTagsText}
          onPaste={handleAddTagsText}
        />
      </Form.Item>

      <Form.Item
        label={
          <Row>
            <Col span={24}>
              <Space size={4}>
                <CloseOutlined className="text-error" />
                <Text strong>{t('Remove Tags:')}</Text>
              </Space>
            </Col>

            <Col span={24}>
              <Text type="secondary">{t('Remove one or more tags from Agent records, after this node.')}</Text>
            </Col>
          </Row>
        }
        name={['data', 'analyticTagsRemoved']}
        rules={[atLeastOneRequired]}
      >
        <Select
          mode="tags"
          size="large"
          autoClearSearchValue={false}
          onSearch={setRemoveTagsSearchValue}
          searchValue={removeTagsSearchValue}
          className="scrollable-dropdown-input"
          placeholder={t('Select or enter a tag')}
          options={Object.values(ANALYTIC_TAGS_RECOMMENDATIONS).map(recommendation => ({ label: recommendation, value: recommendation }))}
          onChange={() => {
            setRemoveTagsSearchValue('');
            setTimeout(() => {
              // Hack to validate the other field too
              form.validateFields();
            }, 500);
          }}
          // We added the `dropdownAlign` in order to avoid the overlap between tags list and input field
          // found that here: https://github.com/ant-design/ant-design/issues/12402#issuecomment-469958721
          dropdownAlign={{
            overflow: { adjustY: 0 }
          }}
          open={removeTagsSelectOpen}
          onDropdownVisibleChange={(open) => {
            if (form.getFieldError(['data', 'analyticTagsRemoved']).length > 0) {
              setRemoveTagsSelectOpen(false);
            } else {
              setRemoveTagsSelectOpen(open);
            }
          }}
          onInputKeyDown={handleRemoveTagsText}
          onPaste={handleRemoveTagsText}
        />
      </Form.Item>
    </Fragment>
  );
};

AnalyticTagsNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default AnalyticTagsNodeForm;
